import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Avatar,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  LinearProgress
} from '@mui/material';
import {
  People,
  Security,
  Assessment,
  TrendingUp,
  Settings,
  AdminPanelSettings,
  PersonAdd,
  MoreVert,
  CheckCircle,
  Error,
  Schedule
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';

const AdminDashboard = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const navigate = useNavigate();

  usePageTitle('Admin Dashboard');

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const response = await api.get('/api/admin/stats/');
      setStats(response.data.stats);
      setRecentUsers(response.data.recent_users);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
    <Card 
     sx={{
       height: '100%',
       cursor: onClick ? 'pointer' : 'default',
       transition: 'all 0.2s ease-in-out',
       '&:hover': onClick ? {
         transform: 'translateY(-2px)',
         boxShadow: theme.shadows[4]
       } : {}
     }}
     onClick={onClick}
   >
     <CardContent>
       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
         <Box>
           <Typography color="text.secondary" gutterBottom variant="body2">
             {title}
           </Typography>
           <Typography variant="h4" fontWeight={600}>
             {value}
           </Typography>
           {subtitle && (
             <Typography variant="body2" color="text.secondary">
               {subtitle}
             </Typography>
           )}
         </Box>
         <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
           {icon}
         </Avatar>
       </Box>
     </CardContent>
   </Card>
 );

 if (loading) {
   return (
     <Container maxWidth="xl" sx={{ py: 4 }}>
       <LinearProgress />
       <Typography variant="h6" sx={{ mt: 2 }}>Loading admin dashboard...</Typography>
     </Container>
   );
 }

 return (
   <Container maxWidth="xl" sx={{ py: 4 }}>
     {/* Admin Header */}
     <Paper 
       elevation={0}
       sx={{ 
         p: { xs: 1.5, md: 4 }, 
         mb: 4, 
         borderRadius: 3,
         background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
         color: 'white',
       }}
     >
       <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
         <Box>
           <Typography variant="h3" gutterBottom fontWeight={700}>
             Administrator Dashboard
           </Typography>
           <Typography variant="h6" sx={{ opacity: 0.9 }}>
             System overview and user management
           </Typography>
         </Box>
         <Button
           variant="contained"
           startIcon={<PersonAdd />}
           onClick={() => navigate('/users')}
           sx={{
             bgcolor: 'white',
             color: theme.palette.error.main,
             '&:hover': {
               bgcolor: 'rgba(255,255,255,0.9)'
             }
           }}
         >
           Manage Users
         </Button>
       </Box>
     </Paper>

     {/* Stats Grid */}
     <Grid container spacing={3} sx={{ mb: 4 }}>
       <Grid item xs={12} sm={6} md={3}>
         <StatCard
           title="Total Users"
           value={stats?.total_users || 0}
           icon={<People />}
           color="primary.main"
           subtitle={`+${stats?.new_users_week || 0} this week`}
           onClick={() => navigate('/users')}
         />
       </Grid>
       <Grid item xs={12} sm={6} md={3}>
         <StatCard
           title="Active Users"
           value={stats?.active_users || 0}
           icon={<CheckCircle />}
           color="success.main"
           subtitle={`${stats?.active_today || 0} today`}
         />
       </Grid>
       <Grid item xs={12} sm={6} md={3}>
         <StatCard
           title="Administrators"
           value={stats?.admin_users || 0}
           icon={<AdminPanelSettings />}
           color="warning.main"
           subtitle="System admins"
         />
       </Grid>
       <Grid item xs={12} sm={6} md={3}>
         <StatCard
           title="System Health"
           value={`${stats?.system_health || 0}%`}
           icon={<TrendingUp />}
           color="success.main"
           subtitle="All systems operational"
         />
       </Grid>
     </Grid>

     {/* Admin Actions Grid */}
     <Grid container spacing={3} sx={{ mb: 4 }}>
       <Grid item xs={12} md={8}>
         {/* Recent Users Table */}
         <Paper sx={{ p: 3 }}>
           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
             <Typography variant="h6" fontWeight={600}>
               Recent Users
             </Typography>
             <Button 
               size="small" 
               variant="outlined"
               onClick={() => navigate('/users')}
             >
               View All
             </Button>
           </Box>
           <TableContainer>
             <Table>
               <TableHead>
                 <TableRow>
                   <TableCell>User</TableCell>
                   <TableCell>Role</TableCell>
                   <TableCell>Status</TableCell>
                   <TableCell>Joined</TableCell>
                   <TableCell align="right">Actions</TableCell>
                 </TableRow>
               </TableHead>
               <TableBody>
                 {recentUsers.length === 0 ? (
                   <TableRow>
                     <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                       No recent users found
                     </TableCell>
                   </TableRow>
                 ) : (
                   recentUsers.map((user) => (
                     <TableRow key={user.id}>
                       <TableCell>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                           <Avatar sx={{ width: 32, height: 32, bgcolor: user.is_staff ? 'error.main' : 'primary.main' }}>
                             {user.username[0].toUpperCase()}
                           </Avatar>
                           <Box>
                             <Typography variant="body2" fontWeight={600}>
                               {user.username}
                             </Typography>
                             <Typography variant="caption" color="text.secondary">
                               {user.email || 'No email'}
                             </Typography>
                           </Box>
                         </Box>
                       </TableCell>
                       <TableCell>
                         <Chip
                           label={user.is_staff ? 'Admin' : 'User'}
                           size="small"
                           color={user.is_staff ? 'error' : 'primary'}
                           icon={user.is_staff ? <AdminPanelSettings /> : <People />}
                         />
                       </TableCell>
                       <TableCell>
                         <Chip
                           label={user.is_active ? 'Active' : 'Inactive'}
                           size="small"
                           color={user.is_active ? 'success' : 'default'}
                           icon={user.is_active ? <CheckCircle /> : <Error />}
                         />
                       </TableCell>
                       <TableCell>{user.date_joined}</TableCell>
                       <TableCell align="right">
                         <IconButton 
                           size="small"
                           onClick={() => navigate('/users')}
                         >
                           <MoreVert />
                         </IconButton>
                       </TableCell>
                     </TableRow>
                   ))
                 )}
               </TableBody>
             </Table>
           </TableContainer>
         </Paper>
       </Grid>

       <Grid item xs={12} md={4}>
         {/* Quick Actions */}
         <Paper sx={{ p: 3, mb: 3 }}>
           <Typography variant="h6" fontWeight={600} gutterBottom>
             Quick Actions
           </Typography>
           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
             <Button
               variant="outlined"
               startIcon={<People />}
               fullWidth
               sx={{ justifyContent: 'flex-start' }}
               onClick={() => navigate('/users')}
             >
               Manage Users
             </Button>
             <Button
               variant="outlined"
               startIcon={<Security />}
               fullWidth
               sx={{ justifyContent: 'flex-start' }}
               onClick={() => navigate('/settings')}
             >
               Security Settings
             </Button>
             <Button
               variant="outlined"
               startIcon={<Assessment />}
               fullWidth
               sx={{ justifyContent: 'flex-start' }}
               onClick={() => navigate('/analytics')}
             >
               View Reports
             </Button>
             <Button
               variant="outlined"
               startIcon={<Settings />}
               fullWidth
               sx={{ justifyContent: 'flex-start' }}
               onClick={() => navigate('/settings')}
             >
               System Settings
             </Button>
             <Button
               variant="outlined"
               startIcon={<AdminPanelSettings />}
               fullWidth
               sx={{ justifyContent: 'flex-start' }}
               onClick={() => window.open('/admin/', '_blank')}
             >
               Django Admin
             </Button>
           </Box>
         </Paper>

         {/* System Status */}
         <Paper sx={{ p: 3 }}>
           <Typography variant="h6" fontWeight={600} gutterBottom>
             System Status
           </Typography>
           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="body2">Database</Typography>
               <Chip label="Online" color="success" size="small" />
             </Box>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="body2">API Server</Typography>
               <Chip label="Online" color="success" size="small" />
             </Box>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="body2">Frontend</Typography>
               <Chip label="Online" color="success" size="small" />
             </Box>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="body2">User Sessions</Typography>
               <Chip label={`${stats?.active_today || 0} Active`} color="info" size="small" />
             </Box>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="body2">Last Backup</Typography>
               <Chip label="2 hours ago" color="success" size="small" />
             </Box>
           </Box>
         </Paper>
       </Grid>
     </Grid>

     {/* Recent Activity */}
     <Paper sx={{ p: 3 }}>
       <Typography variant="h6" fontWeight={600} gutterBottom>
         Recent System Activity
       </Typography>
       <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
           <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
             <PersonAdd />
           </Avatar>
           <Box>
             <Typography variant="body2">New user registered: {recentUsers[0]?.username}</Typography>
             <Typography variant="caption" color="text.secondary">2 hours ago</Typography>
           </Box>
         </Box>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
           <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
             <Settings />
           </Avatar>
           <Box>
             <Typography variant="body2">System settings updated</Typography>
             <Typography variant="caption" color="text.secondary">5 hours ago</Typography>
           </Box>
         </Box>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
           <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
             <Schedule />
           </Avatar>
           <Box>
             <Typography variant="body2">Scheduled maintenance completed</Typography>
             <Typography variant="caption" color="text.secondary">1 day ago</Typography>
           </Box>
         </Box>
       </Box>
     </Paper>
   </Container>
 );
};

export default AdminDashboard;